import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSelectedDate } from "../context/SelectedDateContext";
import { SchoolService } from "../services/SchoolService";
import { ReservationService } from "../services/ReservationService";
import { AppVariablesService } from "../services/AppVariablesService";
import Logo from "../components/Logo";
import BackButton from "../components/BackButton";

export default function ConfirmReservation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedDate, setSelectedDate, selectedSchool, setSelectedSchool } = useSelectedDate();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [reservedSlots, setReservedSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNovember2025Calendar, setShowNovember2025Calendar] = useState(false);
  const schoolService = new SchoolService();
  const reservationService = new ReservationService();
  const appVariablesService = new AppVariablesService();

  useEffect(() => {
    const fetchAppVariables = async () => {
      try {
        const response = await appVariablesService.find();
        if (response?.config?.showNovember2025Calendar !== undefined) {
          setShowNovember2025Calendar(response.config.showNovember2025Calendar);
        }
      } catch (err) {
        console.error("Error fetching app variables:", err);
      }
    };
    fetchAppVariables();
  }, []);

  useEffect(() => {
    const dateParam = searchParams.get('date');
    const schoolIdParam = searchParams.get('schoolId');

    const initializeData = async () => {
      try {
        let currentDate = null;

        // Always set the date from URL if available
        if (dateParam) {
          // Correctly parse the date string as local time to avoid timezone shift.
          // YYYY-MM-DD is parsed as UTC, which can result in the previous day.
          const date = new Date(dateParam + 'T00:00:00');
          setSelectedDate(date);
          currentDate = date;
        }

        // Always fetch school data and available slots if we have an ID
        if (schoolIdParam && dateParam) {
          console.log("schoolIdParam", schoolIdParam);

          // Fetch available slots for the school and date
          const slotsData = await reservationService.getAvailableSlots(schoolIdParam, dateParam);
          setSelectedSchool(slotsData.school);
          setAvailableSlots(slotsData.availableSlots || []);

          // If there are no available slots but there are reservations, show appropriate message
          if (slotsData.availableSlots.length === 0 && slotsData.hasReservations) {
            setErrorMessage("Todos os horários desta escola já estão reservados para a data selecionada.");
          }
        } else if (schoolIdParam) {
          // If we only have school ID but no date, just fetch school data
          const school = await schoolService.getSchoolById(schoolIdParam);
          setSelectedSchool(school);
        }
      } catch (error) {
        const msg = error?.message || error?.error?.message || "Erro ao carregar dados da escola. Tente novamente.";
        setErrorMessage(msg);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="container p-5">
        <div className="text-center">Carregando...</div>
      </div>
    );
  }

  if (errorMessage && !selectedSchool) {
    return (
      <div className="container p-5">
        <div
          className="d-flex align-items-center justify-content-center position-relative"
          style={{ height: "125px" }}
        >
          <BackButton />
          <Logo center className="mb-4" size="125px" />
        </div>
        <div className="alert alert-danger text-center mt-4" role="alert">
          {errorMessage}
        </div>
      </div>
    );
  }

  if (!selectedDate || !selectedSchool) {
    return (
      <div className="container p-5">
        <div className="text-center">Dados insuficientes para continuar.</div>
      </div>
    );
  }

  const handleSlotClick = async (slot, type) => {
    // Prevent double-clicks
    if (isSubmitting) {
      return;
    }

    // Check if the selected date is November 2025 (unless showNovember2025Calendar is enabled)
    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = selectedDate.getMonth(); // 0-indexed, so 10 = November
    if (!showNovember2025Calendar && selectedYear === 2025 && selectedMonth === 10) {
      setErrorMessage("Reservas para novembro de 2025 estão temporariamente desabilitadas.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(""); // Clear any previous errors

    try {
      // Generate idempotency key based on user session and slot details
      const idempotencyKey = `${selectedSchool._id}-${formatDateLocal(selectedDate)}-${slot.startHour}-${slot.endHour}-${Date.now()}`;
      
      const reservation = {
        schoolId: selectedSchool._id,
        type,
        date: formatDateLocal(selectedDate),
        startHour: slot.startHour,
        endHour: slot.endHour,
        overnight: slot.overnight,
        idempotencyKey: idempotencyKey,
      };

      await schoolService.addReservation(reservation);

      // Add the reserved slot to the state
      setReservedSlots((prev) => [
        ...prev,
        { startHour: slot.startHour, endHour: slot.endHour },
      ]);

      // Formatar data e hora de início e término
      const { startFormatted, endFormatted, isOvernight } = formatReservationDateTime(
        formatDateLocal(selectedDate),
        slot.startHour,
        slot.endHour
      );

      const overnightIndicator = isOvernight ? " (Reserva Pernoite)" : "";

      setSuccessMessage(
        `Reserva realizada com sucesso para ${selectedSchool.name}${overnightIndicator}!\n\n` +
        `Início: ${startFormatted}\n` +
        `Término: ${endFormatted}\n` +
        `Tipo: ${type === "entry" ? "Entrada" : "Saída"}`
      );

      setErrorMessage("");
    } catch (error) {
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Erro ao realizar a reserva. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTimeLabel = (hourString) => {
    const hour = parseInt(hourString.split(":")[0], 10);

    if (hour >= 0 && hour <= 5) return "Madrugada";
    if (hour >= 6 && hour <= 11) return "Manhã";
    if (hour >= 12 && hour <= 17) return "Tarde";
    if (hour >= 18 && hour <= 23) return "Noite";

    return "Horário inválido";
  };

  // Função para verificar se uma reserva é overnight
  const isOvernight = (startHour, endHour) => {
    if (!startHour || !endHour) return false;
    
    const [startHours, startMinutes] = startHour.split(':').map(Number);
    const [endHours, endMinutes] = endHour.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    return endTotalMinutes < startTotalMinutes;
  };

  // Função para formatar a data e hora de início e término
  const formatReservationDateTime = (date, startHour, endHour) => {
    // Parse date string as local time, not UTC
    const startDate = new Date(date + 'T00:00:00');
    const endDate = new Date(date + 'T00:00:00');
    
    const [startHours, startMinutes] = startHour.split(':').map(Number);
    const [endHours, endMinutes] = endHour.split(':').map(Number);
    
    startDate.setHours(startHours, startMinutes, 0, 0);
    endDate.setHours(endHours, endMinutes, 0, 0);
    
    // Se é overnight, adiciona um dia à data de término
    if (isOvernight(startHour, endHour)) {
      endDate.setDate(endDate.getDate() + 1);
    }
    
    const formatDate = (date) => {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    
    const formatTime = (date) => {
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    const startFormatted = `${formatDate(startDate)} às ${formatTime(startDate)}`;
    const endFormatted = `${formatDate(endDate)} às ${formatTime(endDate)}`;
    
    return { startFormatted, endFormatted, isOvernight: isOvernight(startHour, endHour) };
  };

  const isSlotReserved = (slot) => {
    return reservedSlots.some(
      (reserved) =>
        reserved.startHour === slot.startHour &&
        reserved.endHour === slot.endHour
    );
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="container p-5">
      <div
        className="d-flex align-items-center justify-content-center position-relative"
        style={{ height: "125px" }}
      >
        <BackButton />
        <Logo center className="mb-4" size="125px" />
      </div>

      <div className="text-center my-5">
        <h2 className="fw-bold">Selecione o Horário</h2>
        <p className="text-muted mt-2">
          {selectedDate.toLocaleDateString('pt-BR', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </p>
      </div>

      {/* Display success alert */}
      {successMessage && (
        <div className="alert alert-success text-center" role="alert" style={{ whiteSpace: 'pre-line' }}>
          {successMessage}
        </div>
      )}

      {/* Display error alert */}
      {errorMessage && (
        <div className="alert alert-danger text-center" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="text-center my-5">
        {availableSlots.length > 0 ? (
          availableSlots.map((slot, index) => (
            <div key={index} className="row mb-5">
              {!isSlotReserved(slot) && (
                <div
                  className="col-12 mb-4 text-center"
                  style={{ 
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    opacity: isSubmitting ? 0.6 : 1 
                  }}
                  onClick={() => !isSubmitting && handleSlotClick(slot, "entry")}
                >
                  <div className="col-12">
                    <div className="px-3">
                      <div className={`row align-items-center justify-content-center border border-2 rounded-5 pb-3 pt-3 ${isSubmitting ? 'border-secondary bg-light' : 'border-secondary'}`}>
                        <div
                          className="col-12 text-center"
                          style={{ width: "fit-content" }}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Processando...
                            </>
                          ) : (
                            <>
                              {getTimeLabel(slot.startHour)} - Das {slot.startHour}{" "}
                              às {slot.endHour}
                              {isOvernight(slot.startHour, slot.endHour) && (
                                <span className="badge bg-warning text-dark ms-2">Overnight</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <p>{errorMessage || "Sem horários disponíveis para o dia selecionado."}</p>
        )}
      </div>

      {/* Button to navigate to My Reservations */}
      <div className="text-center mt-5">
        <button
          className="btn btn-primary"
          onClick={() => navigate("/my-reservations")}
        >
          Minhas Reservas
        </button>
      </div>
    </div>
  );
}
